import { useEffect, useMemo, useState } from 'react';
import './bank.css';
import {
  fetchAccounts, fetchTransactions, fetchSyncRuns, setInternal,
  matchToPayment, candidatePayments, money,
  fetchCapitalBalances, recordCapitalIn, createCapitalAccount, KIND_LABEL,
  type BankAccount, type BankTxn, type SyncRun, type CapitalBalance,
} from '../lib/bank';

/* =====================================================================================
   Bank: what the account says happened, and what is still unexplained.

   THE SCREEN IS A WORK QUEUE, NOT A STATEMENT. A statement is something you already have
   from the bank. What a business actually needs is the shorter list: money in that has
   not yet been tied to an invoice, because until it is, revenue cannot honestly be
   recognised against it. So the queue leads and the full ledger is the second tab.

   THREE THINGS A LINE CAN BE. Income (match it to a payment and the invoice recognises),
   a transfer between our own accounts (mark it internal: it is neither income nor cost,
   and counting it as either is the usual way a cash figure becomes a lie), or a cost
   (leave it; the accountant categorises it at period end).

   AN EMPTY SCREEN CAN BE CORRECT. Visibility is a per-account allowlist, so a colleague
   who has not been named sees nothing. The empty state says that plainly instead of
   implying the feed has failed, because "no data" and "not your data" are different
   facts and confusing them wastes somebody's afternoon.
   ===================================================================================== */

const day = (d: string) => new Date(d + 'T00:00:00')
  .toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' });
const when = (t: string | null) => t
  ? new Date(t).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
  : 'never';

export function BankView() {
  const [accounts, setAccounts] = useState<BankAccount[]>([]);
  const [txns, setTxns] = useState<BankTxn[]>([]);
  const [runs, setRuns] = useState<SyncRun[]>([]);
  const [tab, setTab] = useState<'queue' | 'all' | 'accounts'>('queue');
  const [account, setAccount] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [matching, setMatching] = useState<BankTxn | null>(null);
  const [candidates, setCandidates] = useState<any[]>([]);
  const [funding, setFunding] = useState<BankTxn | null>(null);
  const [capital, setCapital] = useState<CapitalBalance[]>([]);
  const [newFunder, setNewFunder] = useState('');

  const reload = () => {
    setLoading(true);
    Promise.all([fetchAccounts(), fetchTransactions(), fetchSyncRuns(),
                 fetchCapitalBalances()])
      .then(([a, t, r, c]) => {
        setAccounts(a); setTxns(t); setRuns(r); setCapital(c); setError(null);
      })
      .catch(e => setError(String(e.message ?? e)))
      .finally(() => setLoading(false));
  };
  useEffect(reload, []);

  const shown = useMemo(
    () => account ? txns.filter(t => t.bank_account_id === account) : txns,
    [txns, account]);

  const queue = useMemo(
    () => shown.filter(t => t.direction === 'credit' && !t.is_internal && !t.payment_id
      && (!t.category || t.category === 'revenue' || t.category === 'other')),
    [shown]);

  const totals = useMemo(() => {
    const isFunding = (t: BankTxn) =>
      t.category === 'capital_in' || t.category === 'funding';
    const inn = shown.filter(t => t.direction === 'credit' && !t.is_internal
                                  && !isFunding(t))
      .reduce((s, t) => s + Number(t.amount), 0);
    const out = shown.filter(t => t.direction === 'debit' && !t.is_internal)
      .reduce((s, t) => s + Math.abs(Number(t.amount)), 0);
    return { inn, out, net: inn - out };
  }, [shown]);

  const ccy = accounts.find(a => a.id === account)?.currency
    ?? accounts[0]?.currency ?? 'GBP';

  const openMatch = (t: BankTxn) => {
    setMatching(t);
    candidatePayments(t).then(setCandidates).catch(() => setCandidates([]));
  };

  const doMatch = async (paymentId: string) => {
    if (!matching) return;
    await matchToPayment(matching.id, paymentId);
    setMatching(null); reload();
  };

  const toggleInternal = async (t: BankTxn) => {
    await setInternal(t.id, !t.is_internal); reload();
  };

  if (loading) return <div className="bnk-empty">Reading the bank feed…</div>;

  if (error) return (
    <div className="bnk-empty">
      <h3>The bank feed could not be read</h3>
      <p>{error}</p>
    </div>
  );

  if (!accounts.length) return (
    <div className="bnk-empty">
      <h3>No bank account is visible to you</h3>
      <p>
        Bank visibility is granted per account by name, not by role: an administrator of
        the tenant who has not been named on an account sees nothing here, and that is
        deliberate. If you expect to see an account, ask an owner to add you to it.
      </p>
    </div>
  );

  return (
    <>
      <div className="bnk-head">
        <h2>Bank</h2>
        <div className="bnk-controls">
          <select value={account} onChange={e => setAccount(e.target.value)}>
            <option value="">All accounts</option>
            {accounts.map(a => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </select>
          <span className="bnk-synced">
            Last synced {when(accounts[0]?.last_synced_at ?? null)}
          </span>
        </div>
      </div>

      <div className="bnk-tiles">
        <div className="bnk-tile">
          <div className="k">Money in</div>
          <div className="v">{money(totals.inn, ccy)}</div>
        </div>
        <div className="bnk-tile">
          <div className="k">Money out</div>
          <div className="v">{money(totals.out, ccy)}</div>
        </div>
        <div className="bnk-tile">
          <div className="k">Net</div>
          <div className={'v ' + (totals.net >= 0 ? 'pos' : 'neg')}>
            {money(totals.net, ccy)}
          </div>
        </div>
        <div className="bnk-tile">
          <div className="k">Awaiting match</div>
          <div className="v">{queue.length}</div>
          <div className="s">credits not yet tied to an invoice</div>
        </div>
      </div>

      <div className="bnk-tabs">
        <button className={tab === 'queue' ? 'on' : ''} onClick={() => setTab('queue')}>
          Reconciliation queue ({queue.length})
        </button>
        <button className={tab === 'all' ? 'on' : ''} onClick={() => setTab('all')}>
          All transactions ({shown.length})
        </button>
        <button className={tab === 'accounts' ? 'on' : ''} onClick={() => setTab('accounts')}>
          Accounts &amp; sync
        </button>
      </div>

      {tab === 'queue' && (
        queue.length ? (
          <table className="bnk-table">
            <thead><tr>
              <th>Date</th><th className="num">Amount</th><th>Narrative</th><th></th>
            </tr></thead>
            <tbody>
              {queue.map(t => (
                <tr key={t.id}>
                  <td>{day(t.booked_on)}</td>
                  <td className="num credit">{money(Number(t.amount), t.currency)}</td>
                  <td>{t.description ?? t.reference ?? ''}</td>
                  <td className="bnk-actions">
                    <button onClick={() => openMatch(t)}>Match to a payment</button>
                    <button onClick={() => setFunding(t)}>From a funder</button>
                    <button className="ghost" onClick={() => toggleInternal(t)}>
                      Own transfer
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="bnk-empty">
            <h3>Nothing waiting</h3>
            <p>Every credit is either matched to a payment or marked as a transfer
               between the company's own accounts.</p>
          </div>
        )
      )}

      {tab === 'all' && (
        <table className="bnk-table">
          <thead><tr>
            <th>Date</th><th className="num">Amount</th><th>Narrative</th>
            <th>State</th><th></th>
          </tr></thead>
          <tbody>
            {shown.map(t => (
              <tr key={t.id} className={t.is_internal ? 'muted' : ''}>
                <td>{day(t.booked_on)}</td>
                <td className={'num ' + t.direction}>
                  {money(Number(t.amount), t.currency)}
                </td>
                <td>{t.description ?? t.reference ?? ''}</td>
                <td>
                  {t.is_internal
                    ? <span className="bnk-tag">own transfer</span>
                    : t.payment_id
                      ? <span className="bnk-tag ok">matched</span>
                      : t.direction === 'credit'
                        ? <span className="bnk-tag warn">unmatched</span>
                        : <span className="bnk-tag">cost</span>}
                </td>
                <td className="bnk-actions">
                  <button className="ghost" onClick={() => toggleInternal(t)}>
                    {t.is_internal ? 'Not internal' : 'Own transfer'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {tab === 'accounts' && (
        <>
          <table className="bnk-table">
            <thead><tr>
              <th>Account</th><th>Provider</th><th>Currency</th>
              <th>Access</th><th>Last synced</th>
            </tr></thead>
            <tbody>
              {accounts.map(a => (
                <tr key={a.id}>
                  <td>{a.name}{a.identifier_last4 ? ` ••${a.identifier_last4}` : ''}</td>
                  <td>{a.provider}</td>
                  <td>{a.currency}</td>
                  <td><span className="bnk-tag ok">
                    {a.scope === 'read' ? 'read only' : a.scope}
                  </span></td>
                  <td>{when(a.last_synced_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="bnk-note">
            Forum reads this feed and cannot move money: there is no payment-initiation
            path in the product, and the connection holds a read scope at the provider.
          </p>
          <h3 className="bnk-h3">Funding and what the company owes</h3>
          {capital.length ? (
            <table className="bnk-table">
              <thead><tr>
                <th>Funder</th><th>Kind</th><th className="num">Put in</th>
                <th className="num">Repaid</th><th className="num">Owed now</th>
                <th>Last movement</th>
              </tr></thead>
              <tbody>
                {capital.map(c => (
                  <tr key={c.id}>
                    <td>{c.name}</td>
                    <td>{KIND_LABEL[c.kind] ?? c.kind}</td>
                    <td className="num">{money(Number(c.introduced), c.currency)}</td>
                    <td className="num">{money(Number(c.repaid), c.currency)}</td>
                    <td className="num credit">{money(Number(c.balance), c.currency)}</td>
                    <td>{c.last_movement ? day(c.last_movement) : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="bnk-note">
              No funding recorded. When money arrives from a director or an investor,
              use "From a funder" on the queue rather than "Own transfer": an own
              transfer means between the company's own accounts, and using it for
              somebody's own money erases the company's obligation to repay them.
            </p>
          )}

          <h3 className="bnk-h3">Recent syncs</h3>
          <table className="bnk-table">
            <thead><tr>
              <th>Started</th><th>Status</th><th className="num">Fetched</th>
              <th className="num">New</th><th>Message</th>
            </tr></thead>
            <tbody>
              {runs.map(r => (
                <tr key={r.id}>
                  <td>{when(r.started_at)}</td>
                  <td><span className={'bnk-tag ' + (r.status === 'ok' ? 'ok' : r.status === 'failed' ? 'warn' : '')}>
                    {r.status}
                  </span></td>
                  <td className="num">{r.fetched}</td>
                  <td className="num">{r.inserted}</td>
                  <td>{r.message ?? ''}</td>
                </tr>
              ))}
              {!runs.length && (
                <tr><td colSpan={5} className="bnk-note">No sync has run yet.</td></tr>
              )}
            </tbody>
          </table>
        </>
      )}

      {funding && (
        <div className="bnk-modal" onClick={() => setFunding(null)}>
          <div className="bnk-modal-inner" onClick={e => e.stopPropagation()}>
            <h3>{money(Number(funding.amount), funding.currency)} from a funder</h3>
            <p className="bnk-note">
              {day(funding.booked_on)} · {funding.description}
            </p>
            <p className="bnk-note">
              This is money the company did not earn: it is owed back to whoever put it
              in, or it bought them shares. Recording it here keeps that obligation
              visible and keeps it out of revenue.
            </p>
            {capital.length ? (
              <ul className="bnk-cands">
                {capital.map(c => (
                  <li key={c.id}>
                    <span>{c.name} · {KIND_LABEL[c.kind] ?? c.kind}
                      {' '}(owed {money(Number(c.balance), c.currency)})</span>
                    <button onClick={async () => {
                      /* A refused write must say so, not freeze the dialog. */
                      try { await recordCapitalIn(funding, c.id); setFunding(null); reload(); }
                      catch (e) { window.alert(String((e as Error).message)); }
                    }}>Record</button>
                  </li>
                ))}
              </ul>
            ) : null}
            <div className="bnk-newfunder">
              <input
                value={newFunder}
                placeholder="Or a new funder's name"
                onChange={e => setNewFunder(e.target.value)} />
              <button disabled={!newFunder.trim()} onClick={async () => {
                try {
                  const id = await createCapitalAccount({
                    name: newFunder.trim(), kind: 'director_loan',
                    currency: funding.currency });
                  await recordCapitalIn(funding, id);
                  setNewFunder(''); setFunding(null); reload();
                } catch (e) { window.alert(String((e as Error).message)); }
              }}>Open a director's loan and record</button>
            </div>
            <div className="bnk-actions">
              <button className="ghost" onClick={() => setFunding(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {matching && (
        <div className="bnk-modal" onClick={() => setMatching(null)}>
          <div className="bnk-modal-inner" onClick={e => e.stopPropagation()}>
            <h3>Match {money(Number(matching.amount), matching.currency)} received
                {' '}{day(matching.booked_on)}</h3>
            <p className="bnk-note">{matching.description}</p>
            {candidates.length ? (
              <ul className="bnk-cands">
                {candidates.map(c => (
                  <li key={c.id}>
                    <span>{day(c.received_on)} · {money(Number(c.amount), c.currency)}
                      {c.bank_reference ? ` · ${c.bank_reference}` : ''}</span>
                    <button onClick={() => doMatch(c.id)}>Match</button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="bnk-note">
                No payment on the ledger is close to this amount in the same currency.
                Record the payment in Ledger first, then match it here: an invented match
                recognises revenue against the wrong invoice, and nobody looks at it twice.
              </p>
            )}
            <div className="bnk-actions">
              <button className="ghost" onClick={() => setMatching(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
